import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSupplierOnlineShopCreateMutation, useSupplierOnlineShopUpdateMutation } from 'api';
import { Form, FormProps, notification } from 'components';
import { ErrorMappingValueType } from 'typings';
import { IEditPricing } from './index';
import { getUpdatedPayload } from 'utils/helpers';

export type SupplierOnlineShopType = {
  id: string;
  supplierId: string;
  webSiteLink: string;
  login: string;
  password: string;
};

export const useOnlineShopAddEditModal = (props: IEditPricing) => {
  const { onModalStateChange, modalState, dataToEdit } = props;
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };

  const [addOnlineShop, { isLoading: addIsLoading }] = useSupplierOnlineShopCreateMutation();
  const [updateOnlineShop, { isLoading: updateIsLoading }] = useSupplierOnlineShopUpdateMutation();

  const [notificationApi, contextHolder] = notification.useNotification();
  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
  }, [form, onModalStateChange]);

  const onFinish: FormProps<SupplierOnlineShopType>['onFinish'] = useCallback(
    async (values: SupplierOnlineShopType) => {
      const payload = getUpdatedPayload(values, dataToEdit);
      payload.supplierId = id;
      payload.id = dataToEdit?.id;

      try {
        const { data, error }: Record<string, any> = await (dataToEdit ? updateOnlineShop : addOnlineShop)(payload);
        if (data?.success) {
          openNotificationWithIcon({
            type: 'success',
            message: `Supplier online shop was successfully ${dataToEdit ? 'updated' : 'created'}.`,
          });
          onCancel();
        } else if (error?.data?.success === false) {
          Object.values(error?.data?.data || {}).forEach((message) => {
            openNotificationWithIcon({
              type: 'error',
              message: message as string,
            });
          });
        } else if (error?.data?.error) {
          openNotificationWithIcon({
            type: 'error',
            message: 'Something went wrong!',
          });
        }
      } catch (err) {
        console.error(`Error ${dataToEdit ? 'updating' : 'creating'}  supplier online shop:`, err);
        openNotificationWithIcon({
          type: 'error',
          message: 'An unexpected error occurred. Please try again.',
        });
      }
    },

    [addOnlineShop, openNotificationWithIcon, id, onCancel, dataToEdit, updateOnlineShop]
  );

  useEffect(() => {
    if (modalState && dataToEdit) {
      form.setFieldsValue(dataToEdit);
    }
  }, [modalState, form, dataToEdit]);

  return {
    contextHolder,
    form,
    isLoading: addIsLoading || updateIsLoading,
    onFinish,
    onCancel,
  };
};
